import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StepList } from '@features/scripts/components/StepList';
import { useStepArray } from '@features/scripts/hooks/useStepArray';
import type { Robot } from '@api/bff/stationDevices';
import {
  DEFAULT_MASTER_SETUP,
  DEFAULT_MASTER_TEARDOWN
} from '../constants';

type Props = {
  open: boolean;
  initial: Robot | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (robot: Robot) => void;
};

const ROBOT_STEP_TYPES = ['command', 'wait'] as const;

const EMPTY: Robot = {
  id: '',
  label: '',
  stationId: '',
  deviceType: '',
  mqttDeviceId: '',
  setupSteps: [],
  teardownSteps: []
};

const RobotFormDialog = ({
  open,
  initial,
  isSaving,
  onClose,
  onSubmit
}: Props) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<Robot>(EMPTY);
  const setupArray = useStepArray();
  const teardownArray = useStepArray();
  const { reset: resetSetupSteps, toScriptSteps: getSetupSteps } = setupArray;
  const { reset: resetTeardownSteps, toScriptSteps: getTeardownSteps } =
    teardownArray;

  // A robot saved before master steps existed has an empty list, but the backend substitutes
  // RobotMasterSteps.DefaultSetup/DefaultTeardown at run time. Showing an empty list here told the
  // operator "no power steps" while the robot powered up anyway — so show what will actually run.
  const usesDefaultSetup = !initial?.setupSteps?.length;
  const usesDefaultTeardown = !initial?.teardownSteps?.length;

  useEffect(() => {
    setForm(initial ? { ...initial } : EMPTY);
    resetSetupSteps(
      initial?.setupSteps?.length ? initial.setupSteps : DEFAULT_MASTER_SETUP
    );
    resetTeardownSteps(
      initial?.teardownSteps?.length
        ? initial.teardownSteps
        : DEFAULT_MASTER_TEARDOWN
    );
  }, [initial, open, resetSetupSteps, resetTeardownSteps]);

  const updateField = useCallback((field: keyof Robot, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const canSubmit =
    form.stationId.trim() !== '' &&
    form.deviceType.trim() !== '' &&
    form.mqttDeviceId.trim() !== '';

  const handleSubmit = useCallback(() => {
    if (canSubmit)
      onSubmit({
        ...form,
        setupSteps: getSetupSteps(),
        teardownSteps: getTeardownSteps()
      });
  }, [canSubmit, form, onSubmit, getSetupSteps, getTeardownSteps]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {initial
          ? t('settings:stationDevices.dialog.editTitle')
          : t('settings:stationDevices.dialog.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            label={t('settings:stationDevices.form.label')}
            value={form.label}
            onChange={e => updateField('label', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. Bench A"
          />
          <TextField
            label={t('settings:stationDevices.form.stationId')}
            value={form.stationId}
            onChange={e => updateField('stationId', e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="e.g. DEV-STATION-02"
            helperText={t('settings:stationDevices.form.stationIdHelper')}
          />
          <Divider />
          <TextField
            label={t('settings:stationDevices.form.deviceType')}
            value={form.deviceType}
            onChange={e => updateField('deviceType', e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="e.g. calibrator"
          />
          <TextField
            label={t('settings:stationDevices.form.mqttDeviceId')}
            value={form.mqttDeviceId}
            onChange={e => updateField('mqttDeviceId', e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="e.g. 5520a-001"
            helperText={t('settings:stationDevices.form.mqttDeviceIdHelper')}
          />

          <Divider />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings:stationDevices.form.masterSetupHelper')}
            </Typography>
            {usesDefaultSetup && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {t('settings:stationDevices.form.usingDefaultSetup')}
              </Alert>
            )}
            <StepList
              steps={setupArray.steps}
              sectionLabel={t('settings:stationDevices.form.masterSetup')}
              allowedStepTypes={[...ROBOT_STEP_TYPES]}
              onAddStep={setupArray.addStep}
              onRemoveStep={setupArray.removeStep}
              onUpdateStep={setupArray.updateStep}
              onReorder={setupArray.reorderSteps}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings:stationDevices.form.masterTeardownHelper')}
            </Typography>
            {usesDefaultTeardown && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {t('settings:stationDevices.form.usingDefaultTeardown')}
              </Alert>
            )}
            <StepList
              steps={teardownArray.steps}
              sectionLabel={t('settings:stationDevices.form.masterTeardown')}
              allowedStepTypes={[...ROBOT_STEP_TYPES]}
              onAddStep={teardownArray.addStep}
              onRemoveStep={teardownArray.removeStep}
              onUpdateStep={teardownArray.updateStep}
              onReorder={teardownArray.reorderSteps}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t('settings:stationDevices.dialog.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || isSaving}
        >
          {t('settings:stationDevices.dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RobotFormDialog;
